import React, { FunctionComponent, useMemo } from 'react';
import { Box } from '@material-ui/core';
import MarketingLabel from '../../../../../common/components/marketingLabel/MarketingLabel';
import useIsMobile from '../../../../../common/react/useIsMobile';
import SliderTitleOfferContainer from '../variantSlider/sliderTitleOffer/SliderTitleOfferContainer';
import VariantSlider from '../../../../../common/components/variantSlider/VariantSlider';
import { ImageType } from '../../../../../gqlCommon/enum/ImageType';
import ThreeSixtyContainer from '../threeSixtyContainer/ThreeSixtyContainer';
import imagesService from '../../../../service/imagesService';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import IParams from '../../../../model/IParams';
import { useParams } from 'react-router-dom';
import DebugToolbar from '../../../../../gqlCommon/components/debugToolbar/DebugToolbar';
import useCustomizeCarContext from '../../../../hooks/useCustomizeCarContext';
import useNavigationBarContext from '../../../../hooks/useNavigationBarContext';

const MainImageDisplayContainer: FunctionComponent = () => {
  const isMobile = useIsMobile();
  const {
    platformConfig: { showThreeSixtyImages, first360Image },
  } = usePlatformConfig();
  const { trimCode } = useParams<IParams>();
  const decodedTrimCode = decodeURIComponent(trimCode);
  const { loading, selectedCar, error } = useCustomizeCarContext();
  const { selectedActiveSectionName } = useNavigationBarContext();

  const carImages = selectedCar?.images ?? [];
  const selectedSectionImagePov = selectedCar?.availableFeatureCategories?.find(
    cat => cat.code === selectedActiveSectionName,
  )?.attributes.imagePov;
  const imageTypes = useMemo(() => selectedCar?.images.map(image => image.type), [selectedCar]);
  const hasExterior = useMemo(() => imageTypes?.map(type => type.includes(ImageType.EXTERIOR_360)), [imageTypes]);
  const hasInterior =
    !loading && imageTypes?.includes(showThreeSixtyImages ? ImageType.INTERIOR_360_PANORAMIC : ImageType.INTERIOR);
  const interiorImageUrl = hasInterior
    ? imagesService.panoramicInteriorImage(selectedCar?.images)
    : imagesService.interiorImage(selectedCar?.images);
  const exteriorImagesUrls = imagesService.exteriorImagesUrls(selectedCar?.images, first360Image);

  const carImagesWithout360 = useMemo(
    () =>
      carImages?.filter(
        image => !image.type.includes(ImageType.EXTERIOR_360) && image.type !== ImageType.INTERIOR_360_PANORAMIC,
      ),
    [carImages],
  );

  if (hasExterior && showThreeSixtyImages) {
    return (
      <>
        <DebugToolbar codes={{ car_code: selectedCar?.car.code, trim_code: decodedTrimCode }} />
        <ThreeSixtyContainer
          loading={loading}
          hasInterior={hasInterior}
          interiorImageUrl={interiorImageUrl}
          exteriorImagesUrls={exteriorImagesUrls}
          titleComponent={<SliderTitleOfferContainer />}
        />
      </>
    );
  }

  return (
    <>
      {selectedCar && (
        <>
          <DebugToolbar codes={{ car_code: selectedCar?.car.code, trim_code: decodedTrimCode }} />
          <VariantSlider
            images={carImagesWithout360 ?? []}
            error={error}
            loading={loading}
            alt={''}
            selectedSection={selectedActiveSectionName}
            selectedSectionImagePov={selectedSectionImagePov}
            interiorImageType={ImageType.INTERIOR}
            titleComponent={<SliderTitleOfferContainer />}
          />
        </>
      )}
      {isMobile && (
        <Box display={'flex'} justifyContent={'center'} flexDirection={'row'}>
          <MarketingLabel content={''} inline={false} data-test={'text:marketing_label'} />
        </Box>
      )}
    </>
  );
};

export default MainImageDisplayContainer;
